// Rule model + text composition for Client Groups.
//
// The rule tree is a recursive group: each group's connector joins its own
// children only, and children mix conditions and nested subgroups up to
// MAX_RULE_DEPTH levels (root = level 1). The six sentence "kinds" (from the
// requirements image) map onto the backend ConditionType surface in
// bookings_utils.client_group_rules:
//
//   got a service or product   -> service_received (service_id) | product_purchased (product_id)
//   saw a staff member         -> service_received (staff_id, any service)
//   have appointments/purchases-> appointment_count | purchase_count (min / max)
//   have spent over/under      -> total_spend (min / max)
//   have been a client since   -> client_since (before / after)
//   all clients                -> all_clients
//
// The kind is stored EXPLICITLY on the condition (`cond.kind`) — `saw` and an
// any-service `got` are both `service_received`, so it cannot be inferred from
// params. The backend ignores `kind` and the in_last_value/in_last_unit fields.

import {
  ConditionType,
  LegacyRuleTree,
  RuleCondition,
  RuleConnector,
  RuleGroup,
  RuleKind,
  RuleNode,
  RuleTree,
} from "../../../types/clientGroups";

export type { RuleKind };

/** Max nesting depth (root group = level 1). Mirrors the backend MAX_DEPTH. */
export const MAX_RULE_DEPTH = 4;

export interface KindOption {
  value: RuleKind;
  label: string;
}

// Reads as "Clients who [label]".
export const RULE_KIND_OPTIONS: KindOption[] = [
  { value: "got", label: "got a service or product" },
  { value: "saw", label: "saw a staff member" },
  { value: "count", label: "have appointments or purchases" },
  { value: "spend", label: "have spent a total amount" },
  { value: "client_since", label: "have been a client since" },
  { value: "all", label: "are anyone (all clients)" },
];

/** Look-up maps (id -> display name) so text reads with real names. */
export interface RuleLabels {
  services?: Record<string, string>;
  products?: Record<string, string>;
  staff?: Record<string, string>;
}

// --- node guards ------------------------------------------------------------

export function isGroup(node: RuleNode | null | undefined): node is RuleGroup {
  return !!node && Array.isArray((node as RuleGroup).children);
}
export function isCondition(
  node: RuleNode | null | undefined,
): node is RuleCondition {
  return !!node && typeof (node as RuleCondition).type === "string";
}

// --- condition <-> kind -----------------------------------------------------

const TYPE_FOR_KIND: Record<RuleKind, ConditionType> = {
  got: "service_received",
  saw: "service_received",
  count: "appointment_count",
  spend: "total_spend",
  client_since: "client_since",
  all: "all_clients",
};

export function kindToCondition(
  kind: RuleKind,
  params: RuleCondition["params"] = {},
): RuleCondition {
  return { kind, type: TYPE_FOR_KIND[kind], params };
}

export function conditionKind(cond: RuleCondition): RuleKind {
  if (cond.kind) return cond.kind;
  switch (cond.type) {
    case "all_clients":
      return "all";
    case "total_spend":
      return "spend";
    case "client_since":
      return "client_since";
    case "appointment_count":
    case "purchase_count":
      return "count";
    case "product_purchased":
      return "got";
    case "service_received":
      return cond.params.staff_id && !cond.params.service_id ? "saw" : "got";
    default:
      return "got";
  }
}

export const emptyCondition = (kind: RuleKind = "got"): RuleCondition =>
  kindToCondition(kind, {});

export const emptyRuleTree = (): RuleTree => ({
  connector: "AND",
  children: [emptyCondition()],
});

export const oppositeConnector = (c: RuleConnector): RuleConnector =>
  c === "AND" ? "OR" : "AND";

export const emptySubgroup = (parent: RuleConnector): RuleGroup => ({
  connector: oppositeConnector(parent),
  children: [emptyCondition()],
});

// --- legacy shape normalization --------------------------------------------

/** Convert a stored tree (recursive OR legacy two-level) into the recursive shape. */
export function normalizeTree(
  rules: RuleTree | LegacyRuleTree | null,
): RuleTree {
  if (!rules) return emptyRuleTree();
  const anyRules = rules as any;
  if (Array.isArray(anyRules.children)) {
    return {
      connector: anyRules.connector ?? "AND",
      children: anyRules.children.map(normalizeNode),
    };
  }
  // legacy: { connector, groups: [{ connector, conditions: [...] }] }
  if (Array.isArray(anyRules.groups)) {
    const groups = anyRules.groups;
    // A single group is the common case; hoist it to the root so the tree
    // reads without a redundant nesting level (and no spurious parentheses).
    if (groups.length === 1) {
      const g = groups[0];
      return {
        connector: g.connector ?? "AND",
        children: (g.conditions ?? []).map(normalizeNode),
      };
    }
    return {
      connector: anyRules.connector ?? "AND",
      children: groups.map((g: any) => ({
        connector: g.connector ?? "AND",
        children: (g.conditions ?? []).map(normalizeNode),
      })),
    };
  }
  return emptyRuleTree();
}

function normalizeNode(node: any): RuleNode {
  if (node && Array.isArray(node.children)) {
    return {
      connector: node.connector ?? "AND",
      children: node.children.map(normalizeNode),
    };
  }
  if (node && Array.isArray(node.conditions)) {
    return {
      connector: node.connector ?? "AND",
      children: node.conditions.map(normalizeNode),
    };
  }
  return node as RuleCondition;
}

// --- path-based, immutable tree edits --------------------------------------
// A path is the list of child indices from the root. [] is the root group.

export function getNodeAt(tree: RuleTree, path: number[]): RuleNode | null {
  let node: RuleNode = tree;
  for (const i of path) {
    if (!isGroup(node) || !node.children[i]) return null;
    node = node.children[i];
  }
  return node;
}

/** Replace the node at `path` with `fn(node)` (root when path is empty). */
export function updateNodeAt(
  tree: RuleTree,
  path: number[],
  fn: (node: RuleNode) => RuleNode,
): RuleTree {
  if (path.length === 0) return fn(tree) as RuleTree;
  const [i, ...rest] = path;
  const children = tree.children.slice();
  const child = children[i];
  children[i] =
    rest.length === 0
      ? fn(child)
      : isGroup(child)
        ? updateNodeAt(child, rest, fn)
        : child;
  return { ...tree, children };
}

/** Append a child to the group at `groupPath`. */
export function addChildAt(
  tree: RuleTree,
  groupPath: number[],
  node: RuleNode,
): RuleTree {
  return updateNodeAt(tree, groupPath, (g) =>
    isGroup(g) ? { ...g, children: [...g.children, node] } : g,
  ) as RuleTree;
}

/** Remove the node at `path`, then prune any now-empty subgroups (never root). */
export function removeNodeAt(tree: RuleTree, path: number[]): RuleTree {
  if (path.length === 0) return tree;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  const pruned = updateNodeAt(tree, parentPath, (g) =>
    isGroup(g) ? { ...g, children: g.children.filter((_, i) => i !== idx) } : g,
  ) as RuleTree;
  return pruneEmptyGroups(pruned);
}

/** Drop empty subgroups bottom-up; the root is kept even when empty. */
export function pruneEmptyGroups(tree: RuleTree): RuleTree {
  const prune = (group: RuleGroup): RuleGroup => ({
    ...group,
    children: group.children
      .map((c) => (isGroup(c) ? prune(c) : c))
      .filter((c) => !(isGroup(c) && c.children.length === 0)),
  });
  return prune(tree);
}

export function setConnectorAt(
  tree: RuleTree,
  groupPath: number[],
  connector: RuleConnector,
): RuleTree {
  return updateNodeAt(tree, groupPath, (g) =>
    isGroup(g) ? { ...g, connector } : g,
  ) as RuleTree;
}

/** Group depth for a group at `groupPath` (root = 1). */
export const groupDepth = (groupPath: number[]): number => groupPath.length + 1;

/** Every condition in the tree, flattened (for the count summary tiles etc.). */
export function allConditions(tree: RuleTree): RuleCondition[] {
  const out: RuleCondition[] = [];
  const walk = (g: RuleGroup) =>
    g.children.forEach((c) => (isGroup(c) ? walk(c) : out.push(c)));
  walk(tree);
  return out;
}

// --- "got" target (service | product | any) --------------------------------

export type GotTarget = string; // "any_service" | "any_product" | service_id | product_id

export function gotTargetValue(cond: RuleCondition): GotTarget {
  if (cond.type === "product_purchased")
    return cond.params.product_id ?? "any_product";
  return cond.params.service_id ?? "any_service";
}

export function applyGotTarget(
  cond: RuleCondition,
  value: GotTarget,
  isProduct: boolean,
): RuleCondition {
  const params = { ...cond.params };
  delete params.service_id;
  delete params.product_id;
  if (isProduct) {
    if (value !== "any_product") params.product_id = value;
    delete params.staff_id; // products have no staff modifier
    return { ...cond, kind: "got", type: "product_purchased", params };
  }
  if (value !== "any_service") params.service_id = value;
  return { ...cond, kind: "got", type: "service_received", params };
}

// --- "in the last" days/weeks ----------------------------------------------

export function inLast(cond: RuleCondition): {
  value: number | undefined;
  unit: "days" | "weeks";
} {
  const p = cond.params;
  if (p.in_last_value != null)
    return { value: p.in_last_value, unit: p.in_last_unit ?? "days" };
  if (p.in_last_days != null) return { value: p.in_last_days, unit: "days" };
  return { value: undefined, unit: "days" };
}

export function applyInLast(
  cond: RuleCondition,
  value: number | undefined,
  unit: "days" | "weeks",
): RuleCondition {
  const params = { ...cond.params };
  if (value == null) {
    delete params.in_last_value;
    delete params.in_last_unit;
    delete params.in_last_days;
  } else {
    params.in_last_value = value;
    params.in_last_unit = unit;
    params.in_last_days = unit === "weeks" ? value * 7 : value;
  }
  return { ...cond, params };
}

// --- "count" subject (appointments | purchases) ----------------------------

export type CountSubject = "appointments" | "purchases";

export function countSubject(cond: RuleCondition): CountSubject {
  return cond.type === "purchase_count" ? "purchases" : "appointments";
}

export function applyCountSubject(
  cond: RuleCondition,
  subject: CountSubject,
): RuleCondition {
  return {
    ...cond,
    kind: "count",
    type: subject === "purchases" ? "purchase_count" : "appointment_count",
  };
}

// --- Text composition (one generator feeds READS AS, panel + list) ---------

const nfmt = (n: number) => n.toLocaleString();

function svc(id: string | undefined, labels?: RuleLabels): string {
  return (id && labels?.services?.[id]) || "any service";
}
function prod(id: string | undefined, labels?: RuleLabels): string {
  return (id && labels?.products?.[id]) || "any product";
}
function staffName(id: string | undefined, labels?: RuleLabels): string {
  return (id && labels?.staff?.[id]) || "any staff";
}
function fmtDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

// "at least 2 times in the last 180 days" (count noun singularises at one).
function countWindow(cond: RuleCondition): string {
  const { min_count, max_count } = cond.params;
  const { value, unit } = inLast(cond);
  const bits: string[] = [];
  if (min_count != null) bits.push(`at least ${nfmt(min_count)}`);
  if (max_count != null) bits.push(`under ${nfmt(max_count)}`);
  let times = bits.length ? `${bits.join(" and ")} times` : "at least once";
  if (min_count === 1 && max_count == null) times = "at least 1 time";
  if (value != null) times += ` in the last ${nfmt(value)} ${unit}`;
  return times;
}

function eventDates(cond: RuleCondition): string {
  const { after_date, before_date } = cond.params;
  const bits: string[] = [];
  if (after_date) bits.push(`after ${fmtDate(after_date)}`);
  if (before_date) bits.push(`before ${fmtDate(before_date)}`);
  return bits.join(" and ");
}

const withParts = (name: string, parts: string[]): string => {
  const kept = parts.filter(Boolean);
  return kept.length ? `${name} — ${kept.join(", ")}` : name;
};

/** A single condition as a sentence: `Name — p1, p2, p3`. */
export function describeCondition(
  cond: RuleCondition,
  labels?: RuleLabels,
): string {
  const p = cond.params;
  const kind = conditionKind(cond);
  if (kind === "all") return "All clients";
  if (kind === "got") {
    if (cond.type === "product_purchased") {
      return withParts("Bought a product", [
        prod(p.product_id, labels),
        countWindow(cond),
        eventDates(cond),
      ]);
    }
    return withParts("Got a service", [
      svc(p.service_id, labels),
      staffName(p.staff_id, labels),
      countWindow(cond),
      eventDates(cond),
    ]);
  }
  if (kind === "saw") {
    return withParts("Saw a staff member", [
      staffName(p.staff_id, labels),
      countWindow(cond),
      eventDates(cond),
    ]);
  }
  if (kind === "count") {
    const subj =
      countSubject(cond) === "purchases" ? "product purchases" : "appointments";
    const bits: string[] = [];
    if (p.min_count != null) bits.push(`at least ${nfmt(p.min_count)}`);
    if (p.max_count != null) bits.push(`under ${nfmt(p.max_count)}`);
    const range = bits.length ? bits.join(" and ") : "any number of";
    return `Had ${subj} — ${range}`;
  }
  if (kind === "spend") {
    const bits: string[] = [];
    if (p.min != null) bits.push(`over $${nfmt(p.min)}`);
    if (p.max != null) bits.push(`under $${nfmt(p.max)}`);
    return withParts("Spent in total", bits);
  }
  // client_since
  const bits: string[] = [];
  if (p.after) bits.push(`after ${fmtDate(p.after)}`);
  if (p.before) bits.push(`before ${fmtDate(p.before)}`);
  return withParts("Client since", bits);
}

/** A group rendered inline: children joined by the connector, subgroups parenthesised. */
function describeGroupInline(group: RuleGroup, labels?: RuleLabels): string {
  if (!group.children.length) return "any client";
  const conn = ` ${group.connector} `;
  return group.children
    .map((c) =>
      isGroup(c)
        ? `(${describeGroupInline(c, labels)})`
        : describeCondition(c, labels),
    )
    .join(conn);
}

/** The whole tree as one sentence for the READS AS line (root not parenthesised). */
export function readsAs(tree: RuleTree | null, labels?: RuleLabels): string {
  const t = tree ? normalizeTree(tree) : emptyRuleTree();
  if (!t.children.length) return "Clients who match all clients.";
  return `Clients who match ${describeGroupInline(t, labels)}`;
}

/** Compact one-liner for the list Definition cell (same generator). */
export function composeDefinition(
  rules: RuleTree | null,
  labels?: RuleLabels,
): string {
  const t = rules ? normalizeTree(rules) : emptyRuleTree();
  const conds = allConditions(t);
  if (!conds.length || conds.every((c) => conditionKind(c) === "all"))
    return "All clients";
  return describeGroupInline(t, labels);
}

export interface Clause {
  connector: RuleConnector | null; // null for the first clause
  text: string;
}

/** One clause per root child for the detail panel's Definition card. */
export function composeClauses(
  rules: RuleTree | null,
  labels?: RuleLabels,
): Clause[] {
  const t = rules ? normalizeTree(rules) : emptyRuleTree();
  if (!t.children.length)
    return [{ connector: null, text: "All clients are in this group." }];
  return t.children.map((child, i) => ({
    connector: i === 0 ? null : t.connector,
    text: isGroup(child)
      ? `(${describeGroupInline(child, labels)})`
      : describeCondition(child, labels),
  }));
}
